/**
 * Pythagore en carrés — calculs exacts sur un triangle à sommets entiers. PUR.
 *
 * Les sommets sont sur le quadrillage : les carrés des longueurs sont des
 * entiers, l'égalité de Pythagore se teste donc sans arrondi. On teste
 * toujours le PLUS GRAND côté (l'hypoténuse s'il y a un angle droit).
 */

import type { Pt } from '../../ui/frame';
import { exactSqrt } from '../../ui/format';

export type Vertex = 'A' | 'B' | 'C';
export type Triangle = Record<Vertex, Pt>;
export type Side = 'AB' | 'BC' | 'CA';

export const SIDES: readonly Side[] = ['AB', 'BC', 'CA'];
const OPPOSITE: Record<Side, Vertex> = { AB: 'C', BC: 'A', CA: 'B' };

export function ends(side: Side): [Vertex, Vertex] {
  return [side[0] as Vertex, side[1] as Vertex];
}

/** Nom usuel d'un côté vu depuis un sommet : [CA] s'écrit AC depuis A. */
export function sideName(side: Side): string {
  return side === 'CA' ? 'AC' : side;
}

export function squaredLength(t: Triangle, side: Side): number {
  const [p, q] = ends(side);
  const dx = t[q].x - t[p].x;
  const dy = t[q].y - t[p].y;
  return Math.round(dx * dx + dy * dy);
}

/** Longueur exacte : « 5 » si carré parfait, sinon « √29 » (√ du carré, non simplifiée). */
export function lengthText(sq: number): { plain: string; atoms: string[]; exact: boolean } {
  const { plain, atoms, exact } = exactSqrt(sq);
  return { plain, atoms, exact };
}

/** Double de l'aire algébrique (0 : sommets alignés). */
export function cross2(t: Triangle): number {
  return (t.B.x - t.A.x) * (t.C.y - t.A.y) - (t.B.y - t.A.y) * (t.C.x - t.A.x);
}

export interface Analysis {
  degenerate: boolean;
  /** plus grand côté (le premier en cas d'égalité) */
  longest: Side;
  /** sommet opposé au plus grand côté : là où serait l'angle droit */
  vertex: Vertex;
  /** les deux autres côtés */
  legs: [Side, Side];
  longestSq: number;
  legsSq: [number, number];
  right: boolean;
  /** angle au sommet `vertex`, en degrés */
  angleDeg: number;
}

export function analyse(t: Triangle): Analysis {
  const sq = Object.fromEntries(SIDES.map((s) => [s, squaredLength(t, s)])) as Record<Side, number>;
  let longest: Side = 'AB';
  for (const s of SIDES) if (sq[s] > sq[longest]) longest = s;
  const legs = SIDES.filter((s) => s !== longest) as [Side, Side];
  const vertex = OPPOSITE[longest];
  const [p, q] = ends(longest);
  const ux = t[p].x - t[vertex].x;
  const uy = t[p].y - t[vertex].y;
  const vx = t[q].x - t[vertex].x;
  const vy = t[q].y - t[vertex].y;
  const nu = Math.hypot(ux, uy);
  const nv = Math.hypot(vx, vy);
  const cos = nu && nv ? (ux * vx + uy * vy) / (nu * nv) : 1;
  const degenerate = cross2(t) === 0;
  const legsSq: [number, number] = [sq[legs[0]], sq[legs[1]]];
  return {
    degenerate,
    longest,
    vertex,
    legs,
    longestSq: sq[longest],
    legsSq,
    right: !degenerate && legsSq[0] + legsSq[1] === sq[longest],
    angleDeg: (Math.acos(Math.max(-1, Math.min(1, cos))) * 180) / Math.PI,
  };
}

/**
 * Les 4 sommets du carré construit sur le côté, à l'EXTÉRIEUR du triangle
 * (du côté opposé au troisième sommet).
 */
export function outerSquare(t: Triangle, side: Side): [Pt, Pt, Pt, Pt] {
  const [pv, qv] = ends(side);
  const p = t[pv];
  const q = t[qv];
  const r = t[OPPOSITE[side]];
  let nx = -(q.y - p.y);
  let ny = q.x - p.x;
  if ((r.x - p.x) * nx + (r.y - p.y) * ny > 0) {
    nx = -nx;
    ny = -ny;
  }
  return [p, q, { x: q.x + nx, y: q.y + ny }, { x: p.x + nx, y: p.y + ny }];
}

/** Défis : on cherche un triangle ; réussi dès que la figure convient. */
export interface PythagorasGoal {
  id: string;
  text: string;
  check: (t: Triangle) => boolean;
}

function axisAligned(t: Triangle): boolean {
  return SIDES.some((s) => {
    const [p, q] = ends(s);
    return t[p].x === t[q].x || t[p].y === t[q].y;
  });
}

export const GOALS: readonly PythagorasGoal[] = [
  {
    id: 'right-at-a',
    text: 'Rends le triangle rectangle en A.',
    check: (t) => {
      const a = analyse(t);
      return a.right && a.vertex === 'A';
    },
  },
  {
    id: 'tilted',
    text: 'Un triangle rectangle dont aucun côté n’est horizontal ni vertical.',
    check: (t) => analyse(t).right && !axisAligned(t),
  },
  {
    id: 'hyp-5',
    text: 'Un triangle rectangle penché dont l’hypoténuse mesure exactement 5.',
    check: (t) => {
      const a = analyse(t);
      return a.right && a.longestSq === 25 && !axisAligned(t);
    },
  },
  {
    id: 'almost-right',
    text: 'Presque rectangle en C : un angle en C à moins de 6° de l’angle droit, sans l’être. L’égalité tient-elle ?',
    check: (t) => {
      const a = analyse(t);
      const ab = squaredLength(t, 'AB');
      const strictlyLongest = ab > squaredLength(t, 'CA') && ab > squaredLength(t, 'BC');
      return !a.degenerate && strictlyLongest && !a.right && Math.abs(a.angleDeg - 90) < 6;
    },
  },
];
